// SuperAdminHealthPage.jsx
//
// Deployment health dashboard. Calls health_check_deployment() (migration
// 153) and renders the result as a categorized list of checks, each with
// a green / red / yellow chip and a remediation hint if it's not OK.
//
// What this catches:
//   * "Did I forget to apply migration X?" — the most common deployment
//     foot-gun. Each migration we care about has a signature artifact
//     (table, column, or function) that the RPC introspects.
//   * Missing pgcrypto extension on a fresh Supabase project.
//   * Schema drift between branches — a column or function that should
//     exist but doesn't.
//
// What this does NOT catch:
//   * Partially-applied migrations (the signature artifact is present
//     but other artifacts in the same migration didn't apply). For
//     critical migrations, the audit-test pattern in our Jest suite
//     covers structural completeness — those tests pass at build time.
//   * Data-level issues (RLS misconfiguration, orphaned rows, etc.).
//     Those need different tooling.

import React, { useEffect, useState } from 'react';
import {
  HeartPulse, RefreshCw, CheckCircle2, AlertCircle, AlertTriangle,
  ChevronRight as ChevRight, Wrench, ClipboardCopy,
} from 'lucide-react';
import { healthCheckDeployment, healthCheckSupplementary, listAppliedMigrations } from '../../lib/db';
// AUTO-GENERATED manifest of every .sql file in supabase/migrations/.
// Source of truth for "what should be applied"; the ledger RPC is
// the source of truth for "what actually got applied". The diff is
// the missing-migrations list. See scripts/build-migrations-manifest.js.
import MIGRATIONS_MANIFEST from '../../lib/migrationsManifest';

// Status metadata. Drives the chip color + icon for each check.
const STATUS_META = {
  ok:      { Icon: CheckCircle2,  tone: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40', label: 'OK' },
  missing: { Icon: AlertCircle,   tone: 'bg-red-900/40 text-red-300 border-red-700/40',             label: 'Missing' },
  warn:    { Icon: AlertTriangle, tone: 'bg-amber-900/40 text-amber-300 border-amber-700/40',       label: 'Warning' },
};

export default function SuperAdminHealthPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [copiedMsg, setCopiedMsg] = useState('');

  const refresh = () => {
    setLoading(true);
    setError('');
    // THREE parallel calls now:
    //   1. healthCheckDeployment   — signature-artifact probes (mig 153)
    //   2. healthCheckSupplementary — body-content probes (mig 161)
    //   3. listAppliedMigrations   — full ledger of applied migrations (mig 162)
    // Calls 1+2 are the curated "is critical thing X working" view;
    // call 3 is exhaustive coverage — every .sql file in the manifest
    // that doesn't appear in the ledger gets surfaced as missing.
    // All three are allowed to fail independently — degraded checks
    // are better than no checks.
    Promise.all([
      healthCheckDeployment(),
      healthCheckSupplementary(),
      listAppliedMigrations(),
    ]).then(([primary, supplementary, ledger]) => {
      const err = primary.error;
      if (err) {
        // The most common error path: the health_check_deployment RPC
        // itself isn't deployed yet (migration 153 not applied). Surface
        // that as a friendly hint, not a wall of PostgREST gibberish.
        const isMissingRpc =
          err.code === 'PGRST202' ||
          /could not find the function/i.test(err.message || '');
        setError(
          isMissingRpc
            ? 'The health-check RPC isn\'t installed yet. Apply migration 153 (supabase/migrations/153_deployment_health_check.sql) to enable this page.'
            : (err.message || 'Could not run health check'),
        );
        setRows([]);
      } else {
        const primaryRows = primary.data || [];
        const supRows     = supplementary.error ? [] : (supplementary.data || []);

        // If supplementary RPC is missing, synthesize a "missing mig 161"
        // row so the dashboard's own missing-migration call-out picks it up.
        const supMissing = supplementary.error && (
          supplementary.error.code === 'PGRST202' ||
          /could not find the function/i.test(supplementary.error.message || '')
        );
        const supSynth = supMissing ? [{
          category:    'Super-admin',
          migration:   '161',
          check_name:  'health_check_supplementary RPC',
          status:      'missing',
          detail:      'Sibling RPC that adds body-content probes for replaced functions. Without it, the dashboard can\'t detect when a function-replacement migration didn\'t ship.',
          remediation: 'Apply supabase/migrations/161_health_check_supplementary.sql',
        }] : [];

        // Same dance for the ledger RPC (mig 162). If missing, surface
        // it so the operator knows the exhaustive coverage isn't active.
        const ledgerMissing = ledger.error && (
          ledger.error.code === 'PGRST202' ||
          /could not find the function/i.test(ledger.error.message || '')
        );
        const ledgerSynth = ledgerMissing ? [{
          category:    'Schema ledger',
          migration:   '162',
          check_name:  'list_applied_migrations RPC + _schema_migrations table',
          status:      'missing',
          detail:      'Exhaustive coverage of every migration via append-only ledger. Without it, only the ~15 curated probe checks are active — the other ~150 migrations fly blind.',
          remediation: 'Apply supabase/migrations/162_schema_migrations_ledger.sql',
        }] : [];

        // Ledger-vs-manifest diff: each file in the manifest that
        // isn't in the ledger gets a "missing" row. Sorted by mig
        // number so they render in chronological order alongside
        // the curated probes.
        const appliedSet = new Set(
          ledgerMissing ? [] : (ledger.data || []).map((r) => r.name),
        );
        const ledgerDiffRows = ledgerMissing ? [] : MIGRATIONS_MANIFEST
          .filter((name) => !appliedSet.has(name))
          .map((name) => {
            // First token before the underscore is the migration number
            // (matches the convention used by the curated checks).
            const match = name.match(/^(\d+[a-z]?)_/);
            const num   = match ? match[1] : 'unknown';
            return {
              category:    'Schema ledger',
              migration:   num,
              check_name:  name,
              status:      'missing',
              detail:      `Migration file present in supabase/migrations/ but not registered in public._schema_migrations. Either the file was never applied, or it shipped before the self-register convention was adopted (mig 162) and the backfill missed it.`,
              remediation: `Apply supabase/migrations/${name}.sql (or, if already applied, run: insert into public._schema_migrations(name) values ('${name}') on conflict do nothing;)`,
            };
          });

        setRows([
          ...primaryRows,
          ...supRows,
          ...supSynth,
          ...ledgerSynth,
          ...ledgerDiffRows,
        ]);
      }
      setLoading(false);
    });
  };

  useEffect(refresh, []);

  // Group rows by category for the table-of-contents-style rendering.
  // The RPC already orders them in a sensible order, so we preserve
  // insertion order here.
  const grouped = rows.reduce((acc, r) => {
    const key = r.category || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  // Counts for the summary KPI cards.
  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, { ok: 0, missing: 0, warn: 0 });

  // Build a list of missing migrations for the "fix this" call-out.
  const missingMigrations = Array.from(
    new Set(
      rows
        .filter((r) => r.status === 'missing' && /^\d+$/.test(r.migration))
        .map((r) => r.migration),
    ),
  ).sort();

  const copySummary = () => {
    const text = rows.map((r) =>
      `${r.status.toUpperCase().padEnd(8)} ${r.category} · ${r.check_name}` +
      (r.status !== 'ok' ? `\n         → ${r.remediation || '(no remediation listed)'}` : ''),
    ).join('\n');
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedMsg('Copied to clipboard');
      setTimeout(() => setCopiedMsg(''), 2500);
    });
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Deployment health</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            One-shot introspection of what's actually present in the database vs what we
            expect from the migrations we've shipped. Run this after every deployment to
            catch "wait, did I apply that migration?" issues before customers do.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {copiedMsg && (
            <span className="text-xs text-emerald-300">{copiedMsg}</span>
          )}
          <button
            onClick={copySummary}
            disabled={loading || rows.length === 0}
            title="Copy a plain-text summary of all checks to the clipboard"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition disabled:opacity-40"
          >
            <ClipboardCopy size={14} /> Copy summary
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Re-run
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading state */}
      {loading && rows.length === 0 && !error && (
        <div className="text-center py-20 text-slate-500">Running health check…</div>
      )}

      {/* Results */}
      {rows.length > 0 && (
        <>
          {/* KPI summary */}
          <div className="grid grid-cols-3 gap-3">
            <KpiCard
              icon={CheckCircle2}
              tone="emerald"
              label="Passing"
              value={counts.ok}
            />
            <KpiCard
              icon={AlertCircle}
              tone={counts.missing > 0 ? 'red' : 'slate'}
              label="Missing"
              value={counts.missing}
            />
            <KpiCard
              icon={AlertTriangle}
              tone={counts.warn > 0 ? 'amber' : 'slate'}
              label="Warnings"
              value={counts.warn}
            />
          </div>

          {/* "Fix it" callout — lists the migrations that need applying. */}
          {missingMigrations.length > 0 && (
            <div className="px-4 py-4 rounded-xl bg-red-900/15 border border-red-800/40">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-900/40 flex items-center justify-center shrink-0">
                  <Wrench size={14} className="text-red-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-200">
                    {missingMigrations.length} migration{missingMigrations.length === 1 ? '' : 's'} need{missingMigrations.length === 1 ? 's' : ''} applying
                  </p>
                  <p className="text-xs text-red-300/80 mt-1 leading-relaxed">
                    Apply these in the Supabase SQL Editor, in numeric order:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {missingMigrations.map((m) => (
                      <code
                        key={m}
                        className="px-2 py-1 rounded bg-red-900/40 text-red-200 text-xs font-mono"
                      >
                        {m}
                      </code>
                    ))}
                  </div>
                  <p className="text-xs text-red-300/60 mt-3 leading-relaxed">
                    File paths: <code className="font-mono">supabase/migrations/&lt;number&gt;_*.sql</code>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Categorized check list */}
          <div className="space-y-5">
            {Object.entries(grouped).map(([category, items]) => (
              <CategorySection key={category} category={category} items={items} />
            ))}
          </div>
        </>
      )}

      {/* Footnote */}
      <p className="text-xs text-slate-500 leading-relaxed">
        Health checks introspect the live database via{' '}
        <code className="font-mono text-slate-400">information_schema</code> and{' '}
        <code className="font-mono text-slate-400">pg_proc</code>. Re-run after every
        migration apply. Note: this catches "did I apply that file?" but not partial
        application or data-level drift — for those, our Jest audit-test suite covers
        structural completeness at build time.
      </p>
    </div>
  );
}

// ─── KPI card ───────────────────────────────────────────────────────

function KpiCard({ icon: Ic, label, value, tone }) {
  const palette = {
    emerald: { bg: 'bg-emerald-900/30', fg: 'text-emerald-300', border: 'border-emerald-800/40', accent: 'text-emerald-400' },
    red:     { bg: 'bg-red-900/30',     fg: 'text-red-300',     border: 'border-red-800/40',     accent: 'text-red-400' },
    amber:   { bg: 'bg-amber-900/30',   fg: 'text-amber-300',   border: 'border-amber-800/40',   accent: 'text-amber-400' },
    slate:   { bg: 'bg-slate-800/60',   fg: 'text-slate-300',   border: 'border-slate-700',      accent: 'text-slate-400' },
  }[tone] || { bg: 'bg-slate-800/60', fg: 'text-slate-300', border: 'border-slate-700', accent: 'text-slate-400' };

  return (
    <div className={`rounded-2xl p-4 border ${palette.border} bg-slate-900`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${palette.bg} ${palette.fg}`}>
          <Ic size={14} />
        </div>
      </div>
      <p className={`text-3xl font-bold tabular-nums ${palette.accent}`}>{value}</p>
    </div>
  );
}

// ─── Category section ───────────────────────────────────────────────

function CategorySection({ category, items }) {
  const passing = items.filter((r) => r.status === 'ok').length;
  const total   = items.length;
  const allPass = passing === total;

  return (
    <div
      className="bg-slate-900 border rounded-2xl overflow-hidden"
      style={{ borderColor: allPass ? 'rgba(16,185,129,0.18)' : 'rgba(148,163,184,0.18)' }}
    >
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-800/40">
        <div className="flex items-center gap-2">
          <HeartPulse
            size={14}
            className={allPass ? 'text-emerald-400' : 'text-slate-400'}
          />
          <h3 className="text-sm font-semibold text-white">{category}</h3>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">
          {passing} / {total} passing
        </span>
      </div>
      <div className="divide-y divide-slate-800">
        {items.map((r, i) => <CheckRow key={`${r.category}-${r.check_name}-${i}`} row={r} />)}
      </div>
    </div>
  );
}

// ─── Single check row ───────────────────────────────────────────────

function CheckRow({ row }) {
  const meta = STATUS_META[row.status] || STATUS_META.warn;
  const Icon = meta.Icon;
  const isOk = row.status === 'ok';
  return (
    <div className={`px-5 py-3.5 ${isOk ? '' : 'bg-red-900/5'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold border whitespace-nowrap mt-0.5 ${meta.tone}`}>
            <Icon size={11} /> {meta.label}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-100">
              {row.check_name}
              {row.migration && row.migration !== 'core' && (
                <span className="ml-2 text-[10px] text-slate-500 font-mono">
                  migration {row.migration}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              {row.detail}
            </p>
            {!isOk && row.remediation && (
              <div className="flex items-start gap-1.5 text-xs mt-2 px-2.5 py-1.5 rounded bg-slate-800/60 border border-slate-700">
                <ChevRight size={11} className="mt-0.5 shrink-0 text-amber-400" />
                <code className="font-mono text-slate-300 whitespace-pre-wrap break-all">
                  {row.remediation}
                </code>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
